import { useGenericMutation, useFetch } from '../react-query';
import { apiClient } from '../../apiHelper';
import { AxiosResponse } from 'axios';

export const dicomSendQueryKeys = {
  all: ['dicom-send'],
  batch: (batchId: string) => [...dicomSendQueryKeys.all, 'batch', batchId],
  job: (jobId: string) => [...dicomSendQueryKeys.all, 'job', jobId],
  studyHistory: (studyUid: string) => [...dicomSendQueryKeys.all, 'history', studyUid],
};

interface SendStudyRequest {
  study_instance_uid: string;
  destination_server_id: number;
}

interface SendSeriesRequest {
  study_instance_uid: string;
  serie_instance_uid: string;
  destination_server_id: number;
}

interface SendInstanceRequest {
  study_instance_uid: string;
  sop_instance_uid: string;
  destination_server_id: number;
}

interface SendBatchResponse {
  batch_id: string;
  study_instance_uid: string;
  destination_server_id: number;
  destination_ae_title: string;
  total_instances: number;
  queued_jobs: number;
  message: string;
}

interface BatchStatusResponse {
  batch_id: string;
  study_instance_uid: string;
  destination_ae_title: string;
  total_jobs: number;
  queued: number;
  in_progress: number;
  completed: number;
  failed: number;
  cancelled: number;
  progress_percentage: number;
  is_complete: boolean;
  jobs: any[];
}


export function useSendStudyToServer(
  onSuccess?: (response: AxiosResponse, data: SendStudyRequest) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const sendStudyFn = async (data: SendStudyRequest) => {
    const response = await apiClient.post('/dicom/send/study', data);
    return response;
  };

  return useGenericMutation(
    sendStudyFn,
    ['send-study', ...dicomSendQueryKeys.all.map(String)],
    'Failed to send study to server',
    'Study queued for sending',
    undefined,
    onSuccess,
    onError,
    notification
  );
}


export function useSendSeriesToServer(
  onSuccess?: (response: AxiosResponse, data: SendSeriesRequest) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const sendSeriesFn = async (data: SendSeriesRequest) => {
    const response = await apiClient.post('/dicom/send/series', data);
    return response;
  };

  return useGenericMutation(
    sendSeriesFn,
    ['send-series', ...dicomSendQueryKeys.all.map(String)],
    'Failed to send series to server',
    'Series queued for sending',
    undefined,
    onSuccess,
    onError,
    notification
  );
}


export function useSendInstanceToServer(
  onSuccess?: (response: AxiosResponse, data: SendInstanceRequest) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const sendInstanceFn = async (data: SendInstanceRequest) => {
    const response = await apiClient.post('/dicom/send/instance', data);
    return response;
  };

  return useGenericMutation(
    sendInstanceFn,
    ['send-instance', ...dicomSendQueryKeys.all.map(String)],
    'Failed to send instance to server',
    'Instance queued for sending',
    undefined,
    onSuccess,
    onError,
    notification
  );
}


export function useGetBatchStatus(batchId: string, includeJobs: boolean = true, config = {}) {
  const getBatchStatusFn = async (): Promise<BatchStatusResponse> => {
    const response = await apiClient.get(
      `/dicom/send/batch/${batchId}?include_jobs=${includeJobs}`
    );
    return response.data?.data;
  };

  return useFetch(
    getBatchStatusFn,
    dicomSendQueryKeys.batch(batchId),
    {
      enabled: !!batchId,
      refetchInterval: 2000,
      ...config,
    }
  );
}


export function useGetJobStatus(jobId: string, config = {}) {
  const getJobStatusFn = async () => {
    const response = await apiClient.get(`/dicom/send/job/${jobId}`);
    return response.data?.data;
  };

  return useFetch(
    getJobStatusFn,
    dicomSendQueryKeys.job(jobId),
    {
      enabled: !!jobId,
      ...config,
    }
  );
}


export function useGetStudySendHistory(
  studyInstanceUid: string,
  page: number = 1,
  itemsPerPage: number = 20,
  config = {}
) {
  const getHistoryFn = async () => {
    const response = await apiClient.get(
      `/dicom/send/study/${studyInstanceUid}/history?page=${page}&items_per_page=${itemsPerPage}`
    );
    return response.data?.data;
  };

  return useFetch(
    getHistoryFn,
    [...dicomSendQueryKeys.studyHistory(studyInstanceUid), page.toString()],
    {
      enabled: !!studyInstanceUid,
      ...config,
    }
  );
}


export function useCancelBatch(
  onSuccess?: (response: AxiosResponse, data: { batchId: string }) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const cancelBatchFn = async (data: { batchId: string }) => {
    const response = await apiClient.post(`/dicom/send/batch/${data.batchId}/cancel`);
    return response;
  };

  return useGenericMutation(
    cancelBatchFn,
    ['cancel-batch', ...dicomSendQueryKeys.all.map(String)],
    'Failed to cancel batch',
    'Batch cancelled successfully',
    undefined,
    onSuccess,
    onError,
    notification
  );
}


export interface PushDestination {
  id: number;
  server_ae_title: string;
  server_host: string;
  server_port: number;
  local_ae_title?: string;
  tls_enabled?: boolean;
  supports_c_store: boolean;
  label?: string;
}


export function useGetPushDestinations(studyInstanceUid?: string, config = {}) {
  const getDestinationsFn = async (): Promise<any> => {
    const params = studyInstanceUid
      ? `?study_instance_uid=${encodeURIComponent(studyInstanceUid)}`
      : '';
    const response = await apiClient.get(`/dicom/destinations${params}`);
    return response.data;
  };

  return useFetch(
    getDestinationsFn,
    ['pacs-push-destinations', studyInstanceUid || 'all'],
    config
  );
}

export function useSendStudyToDestination(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const sendFn = async (data: {
    destinationAeId: number;
    study_instance_uid: string;
  }) => {
    const response = await apiClient.post(
      `/dicom/destinations/${data.destinationAeId}/send/study`,
      { study_instance_uid: data.study_instance_uid }
    );
    return response;
  };

  return useGenericMutation(
    sendFn,
    ['push-study', ...dicomSendQueryKeys.all.map(String)],
    'Failed to send study to destination',
    'Study queued for sending',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

export function useGetPushBatchStatus(batchId?: string, config = {}) {
  const getStatusFn = async (): Promise<any> => {
    const response = await apiClient.get(`/dicom/destinations/batch/${batchId}`);
    return response.data;
  };

  return useFetch(getStatusFn, ['pacs-push-batch', batchId || 'none'], {
    enabled: !!batchId,
    ...config,
  });
}

export function useCancelPushBatch(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const cancelFn = async (data: { batchId: string }) => {
    const response = await apiClient.post(
      `/dicom/destinations/batch/${data.batchId}/cancel`
    );
    return response;
  };

  return useGenericMutation(
    cancelFn,
    ['cancel-push-batch', ...dicomSendQueryKeys.all.map(String)],
    'Failed to cancel the push',
    'Push cancelled',
    undefined,
    onSuccess,
    onError,
    notification
  );
}



export function useEchoDestination(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void
) {
  const echoFn = async (data: { destinationAeId: number }) => {
    const response = await apiClient.post(
      `/dicom/destinations/${data.destinationAeId}/echo`
    );
    return response;
  };

  return useGenericMutation(
    echoFn,
    ['echo-destination'],
    'Could not test the destination',
    'Destination test complete',
    undefined,
    onSuccess,
    onError,
    false
  );
}
